<template>
  <div class="mobile-toolbar" v-if="styles.showEditor && !revisionContent" :style="toolbarStyle">
    <div class="mobile-toolbar__inner">
      <button class="mobile-toolbar__button button" @click="undo" :disabled="!canUndo" v-title="'Undo'">
        <icon-undo></icon-undo>
      </button>
      <button class="mobile-toolbar__button button" @click="redo" :disabled="!canRedo" v-title="'Redo'">
        <icon-redo></icon-redo>
      </button>
      <div class="mobile-toolbar__separator"></div>
      <template v-for="button in pagedownButtons" :key="button.method">
        <button class="mobile-toolbar__button button" @click="pagedownClick(button.method)" v-title="button.title">
          <component :is="button.iconClass"></component>
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import editorSvc from '../services/editorSvc';
import pagedownButtons from '../data/pagedownButtons';

export default {
  computed: {
    ...mapState('content', [
      'revisionContent',
    ]),
    ...mapGetters('layout', [
      'styles',
    ]),
    canUndo() {
      return this.$store.state.layout.canUndo;
    },
    canRedo() {
      return this.$store.state.layout.canRedo;
    },
    pagedownButtons() {
      return pagedownButtons
        .filter(btn => btn.method)
        .map(btn => ({
          ...btn,
          iconClass: `icon-${btn.icon}`,
        }));
    },
    toolbarStyle() {
      // Position above status bar if visible, otherwise at bottom
      const bottom = this.styles.showStatusBar ? 20 : 0;
      return { bottom: `${bottom}px` };
    },
  },
  methods: {
    undo() {
      editorSvc.clEditor.undoMgr.undo();
    },
    redo() {
      editorSvc.clEditor.undoMgr.redo();
    },
    pagedownClick(name) {
      editorSvc.pagedownEditor.uiManager.doClick(name);
    },
  },
};
</script>

<style lang="scss">
@import '../styles/variables.scss';

.mobile-toolbar {
  display: none;
  position: absolute;
  // bottom is set dynamically via toolbarStyle computed property
  left: 0;
  right: 36px; // Leave space for ButtonBar (36px width)
  background-color: $navbar-bg;
  z-index: 5;
  height: 44px;
  overflow: hidden;

  .app--light & {
    background-color: #e8e8e8;
  }

  .app--dark & {
    background-color: #252525;
  }

  .app--sepia & {
    background-color: #e8dcc8;
  }

  .app--nord & {
    background-color: #3b4252;
  }

  .app--solarized-light & {
    background-color: #eee8d5;
  }

  .app--solarized-dark & {
    background-color: #073642;
  }

  .app--dracula & {
    background-color: #21222c;
  }

  .app--monokai & {
    background-color: #1e1f1c;
  }
}

.mobile-toolbar__inner {
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 100%;
  padding: 0 8px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; // Firefox
  -ms-overflow-style: none; // IE/Edge

  &::-webkit-scrollbar {
    display: none; // Chrome/Safari
  }
}

.mobile-toolbar__button {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  padding: 0;
  display: flex !important;
  align-items: center;
  justify-content: center;
  color: #b9b9b9 !important;
  background-color: transparent;
  border-radius: $border-radius-base;
  margin: 0 2px;

  .icon {
    width: 20px;
    height: 20px;
    color: inherit;
    fill: currentColor;
  }

  // Light themes - match navigation bar colors
  .app--light & {
    color: rgba(0, 0, 0, 0.7) !important;
  }

  .app--sepia & {
    color: rgba(91, 70, 54, 0.8) !important;
  }

  .app--solarized-light & {
    color: rgba(88, 110, 117, 0.8) !important;
  }

  // Dark themes - use white like navigation bar
  .app--dark &,
  .app--nord &,
  .app--solarized-dark &,
  .app--dracula &,
  .app--monokai & {
    color: rgba(255, 255, 255, 0.7) !important;
  }

  &:active,
  &:focus,
  &:hover {
    color: #fff !important;
    background-color: $navbar-hover-background;

    // Light themes - match navigation bar hover colors
    .app--light & {
      color: rgba(0, 0, 0, 0.9) !important;
      background-color: rgba(0, 0, 0, 0.1);
    }

    .app--sepia & {
      color: #5b4636 !important;
      background-color: rgba(91, 70, 54, 0.1);
    }

    .app--solarized-light & {
      color: #586e75 !important;
      background-color: rgba(88, 110, 117, 0.1);
    }
  }

  &[disabled] {
    opacity: 0.4;
    cursor: not-allowed;

    &:active,
    &:focus,
    &:hover {
      background-color: transparent;
    }
  }
}

.mobile-toolbar__separator {
  width: 1px;
  height: 24px;
  background-color: rgba(255, 255, 255, 0.2);
  margin: 0 8px;
  flex-shrink: 0;

  .app--light & {
    background-color: rgba(0, 0, 0, 0.15);
  }

  .app--sepia & {
    background-color: rgba(91, 70, 54, 0.2);
  }

  .app--solarized-light & {
    background-color: rgba(101, 123, 131, 0.2);
  }
}

// Show on mobile/tablet - same breakpoint as edit buttons hide
@media (max-width: 870px) {
  .mobile-toolbar {
    display: block;
  }
}
</style>
